/**
 * Returns:
 * true if reduce motion is active;
 * false otherwise.
 *
 * @return {boolean}
 */
function prefersReduceMotion () {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Animate the fadeIn fadeOut given the duration, easing and then
 * call the complete callback on finish.
 *
 * @param {number} duration animation duration in milliseconds
 * @param {function} easing animation easing function
 * @param {function} animate animating function callback
 * @param {function} complete callback on completed animation
 * @returns {function} _animate callback
 */
function animate (duration, easing, animation, complete) {
	let timeStart;

	function _animate (time) {
		if (!timeStart) timeStart = time;
		const timeElapsed = time - timeStart;
		const progress = Math.min(timeElapsed / duration, 1);

		animation(easing(progress));

		if (timeElapsed >= duration) complete && complete();
		else window.requestAnimationFrame(_animate);
	}

	return _animate
}

const ease = {
	/**
	 * Linear easing function
	 * @param {number} progress
	 * @returns {number}
	 */
	linear: function (progress) {
		return progress
	},
	/**
	 * Quadratic easing function
	 * @param {number} progress
	 * @returns {number}
	 */
	quadratic: function (progress) {
		return Math.pow(progress, 2)
	},

	/**
	 * Swing easing function
	 * @param {number} progress
	 * @returns {number}
	 */
	swing: function (progress) {
		return 0.5 - Math.cos(progress * Math.PI) / 2
	},

	/**
	 * Circle easing function
	 * @param {number} progress
	 * @returns {number}
	 */
	circ: function (progress) {
		return 1 - Math.sin(Math.acos(progress))
	},

	/**
	 * Bounce easing function
	 * @param {number} progress
	 * @returns {number}
	 */
	bounce: function (progress) {
		for (var a = 0, b = 1; ; a += b, b /= 2) {
			if (progress >= (7 - 4 * a) / 11) {
				return -Math.pow((11 - 6 * a - 11 * progress) / 4, 2) + Math.pow(b, 2)
			}
		}
	}
};

/**
 * Fade in the current Element setting it's display property and then
 * animating its opacity.
 *
 * @param {number} [duration=250] animation duration in milliseconds
 * @param {string} [easing='linear'] animation easing type
 * @param {function} animation callback to animate the element
 * @param {function} complete callback on completed animation
 * @returns {void}
 */
function fade (duration, opacity, easing, animation, complete) {
	let caller, value;

	// validate duration
	if (duration) {
		caller = animation.name.replace('_', '');
		value = parseInt(duration);

		if (isNaN(value)) {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter 1 ('duration') is not an number.')`)
		}

		if (value < 0) {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter 1 ('duration') is not a valid number.')`)
		}
	}

	// validate opacity
	if (opacity) {
		caller = animation.name.replace('_', '');
		value = parseFloat(opacity);

		if (isNaN(value)) {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter 2 ('opacity') is not an number.')`)
		}

		if (value < 0 || value > 1) {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter 2 ('opacity') is not a valid number.')`)
		}
	}

	// validate easing
	if (easing) {
		caller = animation.name.replace('_', '');
		value = easing;

		if (typeof easing !== 'string') {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter ('easing') is not an string.')`)
		}

		if (typeof ease[value] !== 'function') {
			throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter ('easing') is not a valid easing function.')`)
		}
	}

	// validate callback
	if (complete && typeof complete !== 'function') {
		caller = animation.name.replace('_', '');
		throw new TypeError(`Failed to execute '${caller}' on 'Element': parameter ('complete') is not an function.')`)
	}

	// prefers-reduce-motion
	duration = !prefersReduceMotion() ? parseInt(duration) : 1;

	// start drawing
	const draw = animate(duration, ease[easing], animation, complete);
	window.requestAnimationFrame(draw);
}

export { fade as f };
